import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, Volume2, Users2, ShieldCheck, BookOpen, Heart } from 'lucide-react';
import useSEO from '@/hooks/useSEO';
import { LANGUAGE_CONTENT } from '@/data/languageContent';

/* /learn-:slug-for-kids — search-optimised landing pages targeting parents
   searching for "learn shona for kids", "learn yoruba for kids", etc.
   Re-uses the editorial content from /app/frontend/src/data/languageContent.js
   and frames it as a parent-facing pitch with a clear CTA back to /register
   and to the deeper /languages/:slug hub. */

const META = {
    shona: {
        title: 'Learn Shona for kids — puzzle games that teach Zimbabwe\u2019s heritage language',
        description: 'A calm, family-friendly way for children aged 4\u201316+ to learn Shona. Puzzle-based lessons (word search, crossword, balloon pop) with native-style pronunciation. Free to try \u2014 no card needed.',
        flag: '\uD83C\uDDFF\uD83C\uDDFC',
        heroNative: 'Mhoro, muzukuru!',
        heroEnglish: 'Hello, grandchild!',
    },
    ndebele: {
        title: 'Learn Ndebele for kids — clicks, songs and stories from Matabeleland',
        description: 'Family-led Ndebele lessons for children. Six puzzle types, native-style audio, three click consonants and the warm grammar of respect. Free to try with a parent or carer.',
        flag: '\uD83C\uDDFF\uD83C\uDDFC',
        heroNative: 'Sawubona, mtwana!',
        heroEnglish: 'Hello, child!',
    },
    yoruba: {
        title: 'Learn Yoruba for kids — tones, proverbs and music from West Africa',
        description: 'A warm puzzle-based introduction to Yoruba for children aged 4\u201316+. Native-style audio, three tone levels, family-led play. Free Shona Word Search to start; full Yoruba on Explorer.',
        flag: '\uD83C\uDDF3\uD83C\uDDEC',
        heroNative: '\u1EB8 k\u00E0\u00E1r\u1ECD\u0300, \u1ECDmo!',
        heroEnglish: 'Good morning, child!',
    },
    swahili: {
        title: 'Learn Swahili for kids — East Africa\u2019s lingua franca, the playful way',
        description: 'Family-friendly Swahili puzzles for children. Six puzzle types, native-style pronunciation, words sourced from Kenya, Tanzania and Uganda. Free to try, no card needed.',
        flag: '\uD83C\uDDF0\uD83C\uDDEA',
        heroNative: 'Habari, mtoto!',
        heroEnglish: 'Hello, child!',
    },
    french: {
        title: 'Learn French for kids — short puzzles, real pronunciation, no streaks',
        description: 'A calm, family-led way for children to learn French. Word search, crossword, balloon pop and more, with native-style audio. No streak guilt, no timers, no pressure. Free to start.',
        flag: '\uD83C\uDDEB\uD83C\uDDF7',
        heroNative: 'Bonjour, mon enfant !',
        heroEnglish: 'Hello, my child!',
    },
    portuguese: {
        title: 'Learn Portuguese for kids — Lusophone words from Lisbon to Maputo',
        description: 'Puzzle-based Portuguese lessons for children aged 4\u201316+. Native-style audio in European Portuguese, six puzzle types, family leaderboard. Free to try with a parent or carer.',
        flag: '\uD83C\uDDF5\uD83C\uDDF9',
        heroNative: 'Ol\u00E1, crian\u00E7a!',
        heroEnglish: 'Hello, child!',
    },
    spanish: {
        title: 'Learn Spanish for kids — Castilian and Latin American Spanish, the calm way',
        description: 'Family-friendly Spanish puzzles for children, now live on Linguistic Quest. Castilian and Latin American Spanish, native-reviewed, six puzzle types, family leaderboard. Free to start.',
        flag: '\uD83C\uDDEA\uD83C\uDDF8',
        heroNative: '\u00A1Hola, ni\u00F1o!',
        heroEnglish: 'Hello, child!',
    },
};

export default function LearnLanguage({ slug }) {
    const meta = META[slug];
    const content = LANGUAGE_CONTENT[slug];

    useSEO({
        title: meta?.title || 'Learn a language for kids — Linguistic Quest',
        description: meta?.description || 'Family-friendly puzzle games that teach heritage and world languages to children.',
        path: `/learn-${slug}-for-kids`,
        image: '/brand/og-default.jpg',
        jsonLdId: `learn-${slug}-page`,
        jsonLd: meta && content ? {
            '@context': 'https://schema.org',
            '@type': 'EducationalOccupationalProgram',
            name: meta.title,
            description: meta.description,
            educationalProgramMode: 'online',
            occupationalCategory: 'Language learning for children',
            provider: {
                '@type': 'Organization',
                name: 'Linguistic Quest',
                url: 'https://www.linguisticquest.com/',
            },
            url: `https://www.linguisticquest.com/learn-${slug}-for-kids`,
        } : undefined,
    });

    if (!meta || !content) {
        return <Navigate to="/languages" replace />;
    }

    const langName = slug.charAt(0).toUpperCase() + slug.slice(1);

    return (
        <main className="bg-lq-paper" data-testid={`page-learn-${slug}`}>
            {/* HERO */}
            <section className="relative overflow-hidden grain-bg">
                <div className="hero-orb w-96 h-96 bg-lq-peach-500 -top-20 -right-20" />
                <div className="hero-orb w-80 h-80 bg-lq-purple-300 -bottom-20 -left-10" />
                <div className="relative max-w-6xl mx-auto px-6 pt-14 pb-12 md:pt-20 md:pb-16 grid md:grid-cols-[1.3fr,1fr] gap-10 items-center">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-lq-purple-500" data-testid="learn-eyebrow">
                            <span className="mr-2 text-lg align-middle">{meta.flag}</span> Learn {langName} for kids
                        </p>
                        <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] text-lq-ink-black" data-testid="learn-h1">
                            {langName} for children, the family way.
                        </h1>
                        <p className="mt-5 text-lg text-lq-ink-700 max-w-xl leading-relaxed">
                            {content.tagline}
                        </p>
                        <p className="mt-3 text-base text-lq-ink-700 max-w-xl leading-relaxed">
                            Short puzzles. A few new words. Tap to hear them spoken. No streaks, no timers, no pressure. Just play, with a parent, carer or grandparent alongside.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Link to="/register" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-6 py-3 rounded-full shadow-lg shadow-lq-purple-300/30" data-testid="learn-cta-register">
                                Start free <ArrowRight size={18} />
                            </Link>
                            <Link to={`/languages/${slug}`} className="inline-flex items-center gap-2 bg-white border-2 border-lq-ink-100 hover:border-lq-purple-300 text-lq-ink-900 font-semibold px-6 py-3 rounded-full" data-testid="learn-cta-hub">
                                Explore the {langName} hub
                            </Link>
                        </div>
                        <p className="mt-4 text-xs text-lq-ink-500">No card needed &middot; Free Shona Word Search included &middot; Cancel any time</p>
                    </div>

                    <div className="bg-white rounded-3xl border-2 border-lq-peach-300 shadow-xl shadow-lq-purple-300/20 p-7 text-center" data-testid="learn-hero-card">
                        <div className="text-5xl">{meta.flag}</div>
                        <p className="mt-4 font-display text-2xl text-lq-ink-900 leading-tight"><em>&ldquo;{meta.heroNative}&rdquo;</em></p>
                        <p className="mt-1 text-sm text-lq-ink-500">{meta.heroEnglish}</p>
                        <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-lq-purple-500">
                            <Volume2 size={14} /> Tap any word to hear it
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY THIS LANGUAGE */}
            <section className="max-w-4xl mx-auto px-6 py-14 md:py-20" data-testid="learn-overview">
                <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">About the language</p>
                <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-lq-ink-900">Why {langName} matters for the next generation.</h2>
                <div className="mt-6 prose prose-lg max-w-none text-lq-ink-700">
                    {content.overview.map((para, i) => (
                        <p key={i} className="mt-4 leading-relaxed">{para}</p>
                    ))}
                </div>
                <dl className="mt-8 grid sm:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white rounded-2xl border border-lq-ink-100 p-4">
                        <dt className="text-xs uppercase tracking-wider text-lq-ink-500">Speakers</dt>
                        <dd className="mt-1 font-display text-lg text-lq-ink-900">{content.speakers}</dd>
                    </div>
                    <div className="bg-white rounded-2xl border border-lq-ink-100 p-4">
                        <dt className="text-xs uppercase tracking-wider text-lq-ink-500">Family</dt>
                        <dd className="mt-1 font-display text-lg text-lq-ink-900">{content.family}</dd>
                    </div>
                    <div className="bg-white rounded-2xl border border-lq-ink-100 p-4">
                        <dt className="text-xs uppercase tracking-wider text-lq-ink-500">Where it&rsquo;s spoken</dt>
                        <dd className="mt-1 text-sm text-lq-ink-900">{content.regions}</dd>
                    </div>
                </dl>
            </section>

            {/* SAMPLE WORDS */}
            <section className="bg-lq-purple-50 border-y border-lq-ink-100 py-14 md:py-20">
                <div className="max-w-5xl mx-auto px-6">
                    <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">First words</p>
                    <h2 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-lq-ink-900">Six {langName} words your child will meet on day one.</h2>
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="learn-sample-words">
                        {content.sampleWords.map((w) => (
                            <div key={w.native} className="bg-white rounded-2xl border border-lq-ink-100 p-4 flex items-center justify-between gap-3">
                                <div>
                                    <div className="font-display text-xl text-lq-ink-900">{w.native}</div>
                                    <div className="text-xs text-lq-ink-500">{w.en}</div>
                                </div>
                                <Volume2 size={18} className="text-lq-purple-500 shrink-0" aria-hidden />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TIPS FOR PARENTS */}
            <section className="max-w-4xl mx-auto px-6 py-14 md:py-20" data-testid="learn-tips">
                <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">For parents &amp; carers</p>
                <h2 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-lq-ink-900">How to learn {langName} together at home.</h2>
                <ul className="mt-6 space-y-4">
                    {content.learnTips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-3 bg-white border border-lq-ink-100 rounded-2xl p-4">
                            <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-lq-peach-100 text-lq-peach-600 shrink-0">
                                <BookOpen size={18} />
                            </div>
                            <p className="text-lq-ink-700 leading-relaxed">{tip}</p>
                        </li>
                    ))}
                </ul>
                <div className="mt-8 bg-lq-peach-100 border border-lq-peach-300 rounded-2xl p-5 flex items-start gap-3">
                    <Heart size={20} className="text-lq-peach-600 mt-0.5 shrink-0" />
                    <p className="text-lq-ink-900 leading-relaxed text-sm">
                        <strong>A note from us:</strong> {content.cultureNote}
                    </p>
                </div>
            </section>

            {/* WHAT'S INCLUDED */}
            <section className="bg-white border-y border-lq-ink-100 py-14 md:py-20">
                <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-3 gap-6">
                    {[
                        { icon: BookOpen, title: 'Six puzzle types', body: 'Word Search, Balloon Pop, Crossword, Word Scramble, Cryptogram and Missing Vowel \u2014 all themed by everyday family life.' },
                        { icon: Volume2, title: 'Native-style audio', body: `Tap any ${langName} word to hear it spoken aloud. Audio is generated with ElevenLabs and reviewed by native-speaker contributors.` },
                        { icon: Users2, title: 'A profile per child', body: 'Up to five child profiles on the Global plan. Siblings see each other\u2019s points without sharing real names.' },
                        { icon: ShieldCheck, title: 'Built to ICO Children\u2019s Code', body: 'No behavioural ads, no chat, no public profiles. Only a parent or carer can change the plan or add a child.' },
                        { icon: Heart, title: 'Family leaderboard', body: 'Parents on paid plans play and earn points too. It\u2019s a shared game, not a race.' },
                        { icon: ArrowRight, title: 'No streak pressure', body: 'Skip a week, skip a month \u2014 the puzzles wait for you. Heritage shouldn\u2019t feel like homework.' },
                    ].map((f) => (
                        <div key={f.title} className="bg-lq-paper p-6 rounded-2xl border border-lq-ink-100">
                            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-lq-purple-100 text-lq-purple-700">
                                <f.icon size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-lq-ink-900">{f.title}</h3>
                            <p className="mt-2 text-sm text-lq-ink-700 leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-3xl mx-auto px-6 py-14 md:py-20 text-center">
                <h2 className="font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">
                    Begin {langName} together, today.
                </h2>
                <p className="mt-4 text-lq-ink-700">
                    Free to start &mdash; no card needed. Add the first child profile when you&rsquo;re ready, then invite a grandparent to play along.
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                    <Link to="/register" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-7 py-3.5 rounded-full" data-testid="learn-bottom-cta-register">
                        Start free
                    </Link>
                    <Link to={`/languages/${slug}`} className="inline-flex items-center gap-2 bg-white border-2 border-lq-ink-100 hover:border-lq-purple-300 text-lq-ink-900 font-semibold px-7 py-3.5 rounded-full">
                        Browse the {langName} hub
                    </Link>
                </div>
            </section>
        </main>
    );
}
